/**
 * SystemConnectivity Section Component
 *
 * Displays system connectivity status with healthy/unhealthy/reauth counts.
 */

import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import LinkIcon from '@mui/icons-material/Link'
import { ConnectivityStatus } from '../../../../../services/reportsApi'
import Metric from '../Metric'

interface SystemConnectivityProps {
  data: ConnectivityStatus
}

const SystemConnectivity: React.FC<SystemConnectivityProps> = ({ data }) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LinkIcon color="primary" />
          <Typography variant="h6" component="h2">
            System Connectivity
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Metric
              label="Healthy"
              value={data.totals['healthy'] || 0}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={4}>
            <Metric
              label="Unhealthy"
              value={data.totals['unhealthy'] || 0}
              color="#d32f2f"
            />
          </Grid>
          <Grid item xs={4}>
            <Metric
              label="Reauth"
              value={data.totals['reauthorization_required'] || 0}
              color="#ed6c02"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          By System
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Object.entries(data.by_system).map(([system, systemData]) => (
            <Chip
              key={system}
              label={`${system}: ${systemData.healthy || 0}/${systemData.total}`}
              size="small"
              color={
                (systemData.unhealthy || 0) > 0 ||
                (systemData.reauthorization_required || 0) > 0
                  ? 'warning'
                  : 'default'
              }
              variant="outlined"
            />
          ))}
        </Stack>

        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            component={RouterLink}
            to="/admin/system-connections"
            size="small"
          >
            View Connections
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export default SystemConnectivity
